/**
 * #1025 — what a one-click press actually filled, for the status strip.
 *
 * A press the venue accepted with an order id is watched here. Fill counts
 * come from the account store. Price additionally needs venue provenance:
 * persisted org rows may hold an audit-limit fallback (#1286).
 *
 * The account-store subscription is created on the first press, not at
 * import: many tests replace the account store wholesale, and a store that
 * subscribes at import would break every file that loads it.
 *
 * #1278 — a press belongs to the session it was made in. The scope is the
 * auth identity epoch (user, org, role, sign-out; not a token refresh) plus
 * the account key mode, whose every change — a disconnect nulls it — starts
 * a new account session, as the fill sound counts it. A new scope retires
 * every watch and seen id, and an older press's answer writes nothing.
 */
import type { KalshiFill } from '../types/kalshi';
import { hasPendingKalshiPlacementConfirmation, useAccountStore } from './accountStore';
import { getAuthSessionEpoch } from './authStore';

export interface OneClickFillResult {
  /** Contracts this order has filled, from its fill rows. */
  filled: number;
  /** Contracts the press sent. */
  of: number;
  /** Count-weighted average of the fill prices, side-native cents. Null when
   *  a matched row is on the other side: no price beats a wrong-frame one. */
  priceCents: number | null;
  /** The fills traded at more than one price, so `priceCents` is an average. */
  priceIsAverage: boolean;
  /** Contracts still resting, only from a venue-confirmed open-order row.
   *  The placement fence's own count can be short by an instant fill. */
  restingCount: number | null;
}

export interface FillWatchInput {
  press: number;
  venueOrderId: string;
  side: 'yes' | 'no';
  count: number;
  /** Self-trade partial: nothing more will fill past this. */
  selfTradeFilled?: number;
}

/** `done` is true once nothing more will fill; the watch then closes. */
export type FillWatchApply = (press: number, fill: OneClickFillResult, done: boolean) => void;

interface Watch extends FillWatchInput {
  apply: FillWatchApply;
  fills: Map<string, { count: number; price: number; side: 'yes' | 'no'; priceVerified: boolean }>;
  lastKey: string | null;
}

/** Older resting orders that fill later still reach the log (slice 2). */
const MAX_FILL_WATCHES = 20;
/** Venue ids whose fills a watch has seen, kept past the watch itself. */
const MAX_SEEN_IDS = 200;

const _watches = new Map<string, Watch>();
const _seen = new Set<string>();
let _unsubscribe: (() => void) | null = null;
/** Bumped on every account key-mode change seen by the subscription. */
let _accountSession = 0;
/** The scope the current watches and seen ids belong to. */
let _scope: string | null = null;

function currentScope(): string {
  return `${getAuthSessionEpoch()}:${_accountSession}`;
}

/** Retire everything from an older scope before anything reads or applies. */
function syncScope(): void {
  const scope = currentScope();
  if (scope === _scope) return;
  _watches.clear();
  _seen.clear();
  _scope = scope;
}

/** The session a press is made in; take it before the order is sent. */
export function pressScope(): string {
  ensureSubscribed();
  syncScope();
  return currentScope();
}

/** Is the press's session still the current one? Every strip write of an
 *  answer that arrives after the send checks this first. */
export function pressScopeCurrent(scope: string): boolean {
  return scope === currentScope();
}

function remember(venueOrderId: string): void {
  _seen.delete(venueOrderId);
  _seen.add(venueOrderId);
  if (_seen.size > MAX_SEEN_IDS) {
    const oldest = _seen.values().next().value;
    if (oldest !== undefined) _seen.delete(oldest);
  }
}

function fold(watch: Watch, fills: readonly KalshiFill[]): void {
  for (const f of fills) {
    if (f.orderId !== watch.venueOrderId || watch.fills.has(f.fillId)) continue;
    watch.fills.set(f.fillId, { count: f.count, price: f.price, side: f.side, priceVerified: f.priceSource === 'venue' });
  }
  if (watch.fills.size > 0) remember(watch.venueOrderId);
}

function resultOf(watch: Watch): OneClickFillResult {
  let filled = 0;
  let notional = 0;
  let wrongSide = false;
  let unverifiedPrice = false;
  const prices = new Set<number>();
  for (const f of watch.fills.values()) {
    filled += f.count;
    notional += f.count * f.price;
    prices.add(f.price);
    if (f.side !== watch.side) wrongSide = true;
    if (!f.priceVerified) unverifiedPrice = true;
  }
  const account = useAccountStore.getState();
  const row = (account.openOrders ?? []).find((o) => o.orderId === watch.venueOrderId);
  const confirmed = row != null && !hasPendingKalshiPlacementConfirmation(watch.venueOrderId);
  // A row claiming more than the fills leave unfilled predates them; a done
  // order rests nothing. Either way the next venue read is the authority.
  const unfilled = isDone(watch, filled) ? 0 : watch.count - filled;
  const resting = confirmed && Number.isFinite(row.remainingCount) && row.remainingCount > 0
    && row.remainingCount <= unfilled
    ? row.remainingCount
    : null;
  return {
    filled,
    of: watch.count,
    priceCents: wrongSide || unverifiedPrice || filled <= 0 ? null : notional / filled,
    priceIsAverage: !wrongSide && !unverifiedPrice && prices.size > 1,
    restingCount: resting,
  };
}

/** Nothing more will fill: the sent count, or a self-trade partial's cap. */
function isDone(watch: Watch, filled: number): boolean {
  return filled >= watch.count
    || (watch.selfTradeFilled != null && filled >= watch.selfTradeFilled);
}

function emit(watch: Watch): void {
  if (watch.fills.size === 0) return;
  const fill = resultOf(watch);
  const done = isDone(watch, fill.filled);
  const key = JSON.stringify(fill);
  if (key === watch.lastKey && !done) return;
  watch.lastKey = key;
  if (done) _watches.delete(watch.venueOrderId);
  watch.apply(watch.press, fill, done);
}

function ensureSubscribed(): void {
  if (_unsubscribe) return;
  _unsubscribe = useAccountStore.subscribe((state, prev) => {
    // The boundary first: a set() that changes the key mode and the fills
    // together must not fold the new rows into an old session's watch.
    if (state.keyMode !== prev.keyMode) _accountSession += 1;
    syncScope();
    if (state.recentFills === prev.recentFills && state.openOrders === prev.openOrders) return;
    for (const watch of [..._watches.values()]) {
      fold(watch, state.recentFills ?? []);
      emit(watch);
    }
  });
}

/** Watch a venue-accepted press. Fills already in the store count at once:
 *  an instant fill can reach the browser before the order's own answer. */
export function armFillWatch(input: FillWatchInput, apply: FillWatchApply): void {
  ensureSubscribed();
  syncScope();
  _watches.delete(input.venueOrderId);
  const watch: Watch = { ...input, apply, fills: new Map(), lastKey: null };
  _watches.set(input.venueOrderId, watch);
  while (_watches.size > MAX_FILL_WATCHES) {
    const oldest = _watches.keys().next().value;
    if (oldest === undefined) break;
    _watches.delete(oldest);
  }
  fold(watch, useAccountStore.getState().recentFills ?? []);
  emit(watch);
}

/** A watch has seen a fill for this venue order: evidence it was placed,
 *  even after its rows left the 100-row window. */
export function fillWatchSeen(venueOrderId: string): boolean {
  syncScope();
  return _seen.has(venueOrderId);
}

/** The strip's words for a fill result, e.g. "Filled 10/25 @ 61¢ · 15 resting". */
export function fillText(fill: OneClickFillResult): string {
  const price = fill.priceCents == null
    ? ' · price unavailable'
    : ` @ ${fill.priceIsAverage ? 'avg ' : ''}${Number(fill.priceCents.toFixed(2))}¢`;
  const resting = fill.restingCount != null ? ` · ${fill.restingCount} resting` : '';
  return `Filled ${fill.filled}/${fill.of}${price}${resting}`;
}

export function _resetFillWatchesForTests(): void {
  _unsubscribe?.();
  _unsubscribe = null;
  _watches.clear();
  _seen.clear();
  _accountSession = 0;
  _scope = null;
}
